import { Component, OnDestroy, OnInit, effect, inject, signal } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BreadcrumbService, EnumValues } from '@card-software/library';
import { DeliveryNoteRoles } from '../../../models/roles/roles-group';
import { DeliveryNote, S3Object } from '../../../models/delivery-note/delivery-note';
import { WorkOrder, WorkOrderFabric } from '../../../models/order/work-order';
import { DeliveryNoteProxyService } from '../../../proxies/order/delivery-note.proxy.service';
import { EnumService } from '../../../services/enum.service';
import { AutoCompleteCompleteEvent, AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { lastValueFrom, Subscription } from 'rxjs';
import { MessageService } from 'primeng/api';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { Business } from '../../../models/finance/business';
import { BusinessProxyService } from '../../../proxies/finance/business.proxy.service';
import { NgxFileDropEntry } from 'ngx-file-drop';
import { LocaleHelperService } from '../../../services/locale-helper.service';
import { WorkOrderProxyService } from '../../../proxies/order/work-order.proxy.service';
import { TranslateService } from '@ngx-translate/core';
import { Fabric } from '../../../models/item/fabric';
import { FabricProxyService } from '../../../proxies/item/fabric.proxy.service';
import { ItemMaster } from '../../../models/item/item-master/item-master';
import { YarnSearchProxyService } from '../../../proxies/item/yarn.proxy.service';
import { MatchingItemMasterCommand } from '../../../models/yarn/yarn-search';
import { AuthService } from '@card-software/nexus-utilities';

@Component({
  selector: 'app-delivery-note-details',
  templateUrl: './delivery-note-details.component.html',
  styleUrl: './delivery-note-details.component.scss',
})
export class DeliveryNoteDetailsComponent implements OnInit, OnDestroy {
  private static readonly UOM_KG = 0;
  private static readonly UOM_LB = 3;

  workOrdersToDisplay: WorkOrder[] = [];
  clientsToDisplay: Business[] = [];
  fabricsAvailable: Fabric[] = [];
  itemMastersAvailable: ItemMaster[] = [];

  public deliveryNote!: DeliveryNote;
  public isLoading = 0;
  public isNewDeliveryNote = true;
  public clients: Business[] = [];
  currentFiles = signal<File[]>([]);
  pendingFiles: File[] = []; // Files to be uploaded on save

  listWorkOrderSelected = signal<WorkOrder[]>([]);

  // HANDLE FORMS
  public formArrayItems: FormArray<FormGroup> = new FormArray<FormGroup>([]);
  public inputWorkOrder: FormControl<WorkOrder | null> = new FormControl(null);
  public inputClient: FormControl<Business | null> = new FormControl(null);
  clientDocumentReference = new FormControl('');
  public inputCreatedDate = new FormControl({ value: '', disabled: true });
  public inputIssuedDate = new FormControl({ value: '', disabled: true });
  public totalQuantitiesLbsForm = new FormControl({ value: 0, disabled: true });
  public totalQuantitiesKgsForm = new FormControl({ value: 0, disabled: true });
  public fabricSelection = new FormControl<string | null>(null);
  enumValues: EnumValues = {};

  public deliveryNoteForm = new FormGroup({
    workOrder: this.inputWorkOrder,
    client: this.inputClient,
    clientDocumentReference: this.clientDocumentReference,
    items: this.formArrayItems,
    createdDate: this.inputCreatedDate,
    issuedDate: this.inputIssuedDate,
    totalQuantitiesLbs: this.totalQuantitiesLbsForm,
    totalQuantitiesKgs: this.totalQuantitiesKgsForm,
  });

  private subscription: Subscription = new Subscription();
  private subscriptionFormArray: Subscription = new Subscription();

  private _deliveryNoteProxyService = inject(DeliveryNoteProxyService);
  private _workOrderProxyService = inject(WorkOrderProxyService);
  private businessProxyService = inject(BusinessProxyService);
  private fabricProxyService = inject(FabricProxyService);
  private yarnSearchProxyService = inject(YarnSearchProxyService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private enumService = inject(EnumService);
  private messageService = inject(MessageService);
  public localeHelperService = inject(LocaleHelperService);
  private translateService = inject(TranslateService);
  private breadcrumbService = inject(BreadcrumbService);

  constructor() {
    effect(() => {
      this.enumValues = this.enumService.getEnum('CustomerOrderStatus', 'orderEnums')?.values;
    });
  }

  canEdit() {
    return AuthService.hasRole(DeliveryNoteRoles.DeliveryNoteAdmin);
  }

  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe((params) => {
      const id = params['id'];
      if (!id) this.cancel();
      if (id === 'new' && !this.canEdit()) {
        this.cancel();
        return;
      }
      if (id !== 'new') {
        this.getDeliveryNote(id);
        this.isNewDeliveryNote = false;
      } else {
        this.breadcrumbService.setCurrent('Common_New');
        // Set created date to today for new delivery notes
        this.inputCreatedDate.setValue(this.parseDate(new Date()) as string);
      }
    });

    if (!this.canEdit()) {
      this.deliveryNoteForm.disable();
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.subscriptionFormArray.unsubscribe();
  }

  searchWorkOrder(event: AutoCompleteCompleteEvent) {
    const query = event.query.toLowerCase();

    if (!this.inputClient.value?.id) {
      GlobalMessageUtil.warn('Warning', 'Please select a client first');
      this.workOrdersToDisplay = [];
      return;
    }

    this._workOrderProxyService
      .searchWorkOrder(
        {
          orderNumber: query,
          customerReference: this.inputClient.value.id,
        },
        1,
        25,
      )
      .subscribe({
        next: (response) => {
          if (response.data?.items === null) {
            this.workOrdersToDisplay = [];
            return;
          }
          // Filter out voided work orders (status 7)
          this.workOrdersToDisplay = (response.data?.items ?? []).filter((wo) => wo.status === 2);
        },
        error: (e) => {
          console.error(e);
          this.workOrdersToDisplay = [];
        },
      });
  }

  addWorkOrderAtList(event: AutoCompleteSelectEvent) {
    const WO = event.value;
    if (!WO) return;

    const matchWO = this.listWorkOrderSelected().find((w) => w.id === WO.id);

    if (!matchWO) {
      this.isLoading++;
      this._workOrderProxyService.getWorkOrderById(WO.id!).subscribe({
        next: (response) => {
          this.isLoading--;
          if (response.data) {
            const fullWO = response.data;
            this.listWorkOrderSelected().push(fullWO);
            this.loadFabricsFromSelectedWorkOrders();
          }
        },
        error: (e) => {
          this.isLoading--;
          console.error('Error fetching work order:', e);
        },
      });
    } else {
      GlobalMessageUtil.warn('Warning', 'Work Order already selected');
    }

    this.inputWorkOrder.setValue(null, { emitEvent: false });
  }

  deleteWorkOrderAtList(index: number) {
    const deletedWO = this.listWorkOrderSelected()[index];
    this.listWorkOrderSelected().splice(index, 1);

    // Remove items that belong to the deleted work order
    const itemsToRemove: number[] = [];
    this.formArrayItems.controls.forEach((control, idx) => {
      if (control.get('workOrderReference')?.value === deletedWO.id) {
        itemsToRemove.push(idx);
      }
    });

    itemsToRemove.reverse().forEach((idx) => {
      this.formArrayItems.removeAt(idx);
    });

    this.loadFabricsFromSelectedWorkOrders();
  }

  searchClient(event: AutoCompleteCompleteEvent) {
    const query = event.query.toLowerCase();

    this.isLoading++;
    // First, get all work orders to find clients that have work orders
    this._workOrderProxyService.getWorkOrders().subscribe({
      next: (response) => {
        if (response.data && response.data.length > 0) {
          // Get unique client references from all work orders
          const clientRefsWithWO = [...new Set(response.data.map((wo) => wo.customerReference).filter((ref) => ref))];

          if (clientRefsWithWO.length > 0) {
            // Fetch client details for those with work orders
            this.businessProxyService.getByIds(clientRefsWithWO).subscribe({
              next: (clientsResponse) => {
                this.isLoading--;
                // Filter by query
                this.clientsToDisplay = (clientsResponse.data ?? []).filter((client) => client.name?.toLowerCase().includes(query));
              },
              error: (e) => {
                this.isLoading--;
                console.error('Error fetching clients:', e);
                this.clientsToDisplay = [];
              },
            });
          } else {
            this.isLoading--;
            this.clientsToDisplay = [];
          }
        } else {
          this.isLoading--;
          this.clientsToDisplay = [];
        }
      },
      error: (e) => {
        this.isLoading--;
        console.error('Error getting work orders:', e);
        this.clientsToDisplay = [];
      },
    });
  }

  loadFabricsFromSelectedWorkOrders() {
    const selectedWOs = this.listWorkOrderSelected();
    if (selectedWOs.length === 0) {
      this.fabricsAvailable = [];
      this.itemMastersAvailable = [];
      return;
    }

    // Collect all yarn codes from fabrics in selected work orders
    const yarnCommands: MatchingItemMasterCommand[] = [];
    const fabricCodeToYarnCode = new Map<string, string>();

    selectedWOs.forEach((wo) => {
      wo.fabrics.forEach((fabric) => {
        if (fabric.yarnCertificationDetails && fabric.yarnCertificationDetails.length > 0) {
          fabric.yarnCertificationDetails.forEach((yarnDetail) => {
            if (yarnDetail.yarnCode) {
              yarnCommands.push({
                yarnCode: yarnDetail.yarnCode,
                certificationReference: yarnDetail.certificationReferences, // or appropriate database value
              });
              fabricCodeToYarnCode.set(fabric.fabricCode, yarnDetail.yarnCode);
            }
          });
        }
      });
    });

    if (yarnCommands.length === 0) {
      this.fabricsAvailable = [];
      this.itemMastersAvailable = [];
      return;
    }

    this.isLoading++;
    // Fetch ItemMasters using yarn codes
    this.yarnSearchProxyService.getItemMastersByYarnCode(yarnCommands).subscribe({
      next: (response) => {
        this.isLoading--;
        if (response.data) {
          // Convert Record<string, ItemMaster | null> to ItemMaster[]
          this.itemMastersAvailable = Object.values(response.data).filter((item): item is ItemMaster => item !== null);
        } else {
          this.itemMastersAvailable = [];
        }
      },
      error: (error) => {
        this.isLoading--;
        console.error('Error loading item masters from yarn codes:', error);
        this.itemMastersAvailable = [];
      },
    });
  }

  getAvailableWorkOrdersForItem(itemCode: string): WorkOrder[] {
    return this.listWorkOrderSelected().filter((wo) =>
      wo.fabrics.some((fabric) => {
        if (fabric.yarnCertificationDetails && fabric.yarnCertificationDetails.length > 0) {
          return fabric.yarnCertificationDetails.some((yarnDetail) => {
            // Check if this fabric's yarn code corresponds to the item
            const itemMaster = this.itemMastersAvailable.find((im) => im.itemCode === itemCode);
            return yarnDetail.yarnCode && itemMaster;
          });
        }
        return false;
      }),
    );
  }

  getFabricFromWorkOrder(workOrder: WorkOrder, fabricCode: string): WorkOrderFabric | undefined {
    return workOrder.fabrics.find((f) => f.fabricCode === fabricCode);
  }

  get itemMastersNotYetAdded(): ItemMaster[] {
    const addedItemCodes = this.formArrayItems.controls.map((control) => control.get('itemCode')?.value);
    return this.itemMastersAvailable.filter((item) => !addedItemCodes.includes(item.itemCode));
  }

  get itemMastersForDropdown(): (ItemMaster & { displayLabel: string })[] {
    return this.itemMastersNotYetAdded.map((item) => {
      const description = this.localeHelperService.getLocalizedValue(item.itemDescription);
      return {
        ...item,
        displayLabel: `${item.itemCode} - ${description || ''}`,
      };
    });
  }

  addItemMaster(itemCode: string) {
    if (!itemCode) return;

    // Find the ItemMaster to get description
    const itemMaster = this.itemMastersAvailable.find((im) => im.itemCode === itemCode);
    const description = itemMaster ? this.localeHelperService.getLocalizedValue(itemMaster.itemDescription) : '';

    const formGroup = new FormGroup({
      lineId: new FormControl<string>(''),
      itemCode: new FormControl<string>(itemCode),
      workOrderReference: new FormControl<string | null>(null, [Validators.required]),
      quantity: new FormControl<number>({ value: 0, disabled: !this.canEdit() }),
      description: new FormControl<string>(description),
      itemDescription: new FormControl<string>(description),
      selectedWorkOrder: new FormControl<WorkOrder | null>(null, [Validators.required]),
    });

    this.formArrayItems.push(formGroup);

    // Subscribe to work order selection
    formGroup.get('selectedWorkOrder')?.valueChanges.subscribe((selectedWO: WorkOrder | null) => {
      if (selectedWO) {
        formGroup.get('workOrderReference')?.setValue(selectedWO.id);

        // Find fabric from work order by matching yarn code to item code
        // We need to find which fabric's yarn code corresponds to this item
        const fabric = this.findFabricByItemCode(selectedWO, itemCode);
        if (fabric) {
          formGroup.get('quantity')?.setValue(fabric.quantityInUOM);
        }

        this.getTotalQuantity();
      }
    });

    formGroup.get('quantity')?.valueChanges.subscribe(() => this.getTotalQuantity());

    // Reset the dropdown selection
    this.fabricSelection.reset();
  }

  private findFabricByItemCode(workOrder: WorkOrder, itemCode: string): WorkOrderFabric | undefined {
    // Find the fabric whose yarn code maps to this item code
    return workOrder.fabrics.find((fabric) => {
      if (fabric.yarnCertificationDetails && fabric.yarnCertificationDetails.length > 0) {
        // Check if any yarn code in this fabric matches our item
        return fabric.yarnCertificationDetails.some((yarnDetail) => {
          const itemMaster = this.itemMastersAvailable.find((im) => im.itemCode === itemCode);
          // This is a simplified check - you may need more sophisticated matching
          return yarnDetail.yarnCode && itemMaster;
        });
      }
      return false;
    });
  }

  public getTotalQuantity() {
    let totalQuantitiesLbs = 0;
    let totalQuantitiesKgs = 0;

    this.formArrayItems.controls.forEach((item) => {
      const quantity = item.get('quantity')?.value || 0;

      const itemCode = item.get('itemCode')?.value;
      const itemMaster = this.itemMastersAvailable.find((im) => im.itemCode === itemCode);
      const uom = itemMaster?.unitOfMeasure;
      const isKgs = uom === 0;

      if (isKgs) {
        totalQuantitiesKgs += quantity;
        totalQuantitiesLbs += quantity / 0.453592;
      } else {
        totalQuantitiesLbs += quantity;
        totalQuantitiesKgs += quantity * 0.453592;
      }
    });

    this.deliveryNoteForm.patchValue({ totalQuantitiesLbs, totalQuantitiesKgs }, { emitEvent: false });
  }

  deleteItemArrayForm(index: number) {
    this.formArrayItems.removeAt(index);
    this.getTotalQuantity();
    // Reset the dropdown selection to allow re-adding deleted items
    this.fabricSelection.reset();
  }

  private getDeliveryNote(id: string): void {
    if (!id) return;

    this.isLoading++;
    this._deliveryNoteProxyService.getDeliveryNote(id).subscribe({
      next: (response) => {
        this.isLoading--;
        if (response.data) {
          this.deliveryNote = response.data;
          this.breadcrumbService.setCurrent(this.deliveryNote.number);
          this.loadExistingDeliveryNoteData(this.deliveryNote);
        }
      },
      error: (error) => {
        this.isLoading--;
        console.error('Error loading delivery note:', error);
      },
    });
  }

  private loadExistingDeliveryNoteData(deliveryNote: DeliveryNote) {
    if (!deliveryNote) return;

    // Clear existing data to avoid duplicates when reloading
    this.listWorkOrderSelected.set([]);
    this.formArrayItems.clear();

    this.clientDocumentReference.setValue(deliveryNote.clientDocumentReference ?? '');
    this.inputCreatedDate.setValue(this.parseDate(deliveryNote.createdDate ?? new Date()) as string);
    this.inputIssuedDate.setValue(this.parseDate(deliveryNote.invoiceIssuedDate ?? new Date()) as string);

    // Load client details from backend to ensure complete data
    if (deliveryNote.client?.id) {
      this.isLoading++;
      this.businessProxyService.getById(deliveryNote.client.id).subscribe({
        next: (response) => {
          this.isLoading--;
          if (response.data) {
            this.inputClient.setValue(response.data);
          }
        },
        error: (error) => {
          this.isLoading--;
          console.error('Error loading client:', error);
          // Fallback to the client data from delivery note
          this.inputClient.setValue(deliveryNote.client!);
        },
      });
    } else if (deliveryNote.client) {
      // Fallback if no ID but client object exists
      this.inputClient.setValue(deliveryNote.client);
    }

    // Load work orders
    const woReferences = deliveryNote.workOrderReferences ?? [];
    if (woReferences.length === 0) return;

    this.isLoading++;
    const woPromises = woReferences.map((woRef) => lastValueFrom(this._workOrderProxyService.getWorkOrderById(woRef)));

    Promise.all(woPromises)
      .then((responses) => {
        this.isLoading--;
        responses.forEach((response) => {
          if (response.data) {
            this.listWorkOrderSelected().push(response.data);
          }
        });

        this.loadFabricsFromSelectedWorkOrders();

        setTimeout(() => {
          this.loadDeliveryNoteItems(deliveryNote);
        }, 500);
      })
      .catch((error) => {
        this.isLoading--;
        console.error('Error loading work orders:', error);
      });
  }

  private loadDeliveryNoteItems(deliveryNote: DeliveryNote) {
    if (!deliveryNote.items || deliveryNote.items.length === 0) return;

    this.formArrayItems.clear();

    // Build yarn code commands from work orders to load ItemMasters
    const yarnCommands: MatchingItemMasterCommand[] = [];
    this.listWorkOrderSelected().forEach((wo) => {
      wo.fabrics.forEach((fabric) => {
        if (fabric.yarnCertificationDetails && fabric.yarnCertificationDetails.length > 0) {
          fabric.yarnCertificationDetails.forEach((yarnDetail) => {
            if (yarnDetail.yarnCode) {
              yarnCommands.push({
                yarnCode: yarnDetail.yarnCode,
              });
            }
          });
        }
      });
    });

    if (yarnCommands.length > 0) {
      this.isLoading++;
      this.yarnSearchProxyService.getItemMastersByYarnCode(yarnCommands).subscribe({
        next: (response) => {
          this.isLoading--;
          const itemMasterMap = new Map<string, ItemMaster>();

          if (response.data) {
            Object.values(response.data).forEach((itemMaster) => {
              if (itemMaster) {
                itemMasterMap.set(itemMaster.itemCode, itemMaster);
              }
            });
          }

          // Now create form groups with ItemMaster descriptions
          deliveryNote.items?.forEach((item) => {
            const wo = this.listWorkOrderSelected().find((w) => w.id === item.workOrderReference);
            if (!wo || !item.itemCode) return;

            const itemMaster = itemMasterMap.get(item.itemCode);
            const itemDescription = itemMaster
              ? this.localeHelperService.getLocalizedValue(itemMaster.itemDescription)
              : item.description || '';

            const formGroup = new FormGroup({
              lineId: new FormControl(item.lineId),
              itemCode: new FormControl(item.itemCode),
              workOrderReference: new FormControl(item.workOrderReference),
              quantity: new FormControl({ value: item.quantity, disabled: !this.canEdit() }),
              description: new FormControl(item.description || itemDescription),
              itemDescription: new FormControl(itemDescription),
              selectedWorkOrder: new FormControl(wo),
            });

            formGroup.get('quantity')?.valueChanges.subscribe(() => this.getTotalQuantity());
            this.formArrayItems.push(formGroup);
          });

          this.getTotalQuantity();
        },
        error: (error) => {
          this.isLoading--;
          console.error('Error loading item masters for existing items:', error);

          // Fallback: load items without descriptions
          deliveryNote.items?.forEach((item) => {
            const wo = this.listWorkOrderSelected().find((w) => w.id === item.workOrderReference);
            if (!wo) return;

            const formGroup = new FormGroup({
              lineId: new FormControl(item.lineId),
              itemCode: new FormControl(item.itemCode),
              workOrderReference: new FormControl(item.workOrderReference),
              quantity: new FormControl({ value: item.quantity, disabled: !this.canEdit() }),
              description: new FormControl(item.description),
              itemDescription: new FormControl(item.description || ''),
              selectedWorkOrder: new FormControl(wo),
            });

            formGroup.get('quantity')?.valueChanges.subscribe(() => this.getTotalQuantity());
            this.formArrayItems.push(formGroup);
          });

          this.getTotalQuantity();
        },
      });
    } else {
      // No yarn codes available, load with basic description
      deliveryNote.items.forEach((item) => {
        const wo = this.listWorkOrderSelected().find((w) => w.id === item.workOrderReference);
        if (!wo) return;

        const formGroup = new FormGroup({
          lineId: new FormControl(item.lineId),
          itemCode: new FormControl(item.itemCode),
          workOrderReference: new FormControl(item.workOrderReference),
          quantity: new FormControl({ value: item.quantity, disabled: !this.canEdit() }),
          description: new FormControl(item.description),
          itemDescription: new FormControl(item.description || ''),
          selectedWorkOrder: new FormControl(wo),
        });

        formGroup.get('quantity')?.valueChanges.subscribe(() => this.getTotalQuantity());
        this.formArrayItems.push(formGroup);
      });

      this.getTotalQuantity();
    }
  }

  public async save() {
    if (this.listWorkOrderSelected().length === 0) {
      GlobalMessageUtil.error('Error', 'Please select at least one work order');
      return;
    }

    if (!this.inputClient.value) {
      GlobalMessageUtil.error('Error', 'Please select a client');
      return;
    }

    const deliveryNote: DeliveryNote = {
      number: this.deliveryNote?.number || undefined,
      files: this.deliveryNote?.files || [],
      workOrderReferences: this.listWorkOrderSelected().map((x) => x.id!) ?? [],
      clientDocumentReference: this.clientDocumentReference.value ?? undefined,
      clientReference: this.inputClient.value?.id || undefined,
      items: this.formArrayItems.controls.map((control) => {
        const rawValue = control.getRawValue();
        return {
          lineId: rawValue.lineId || undefined, // Convert empty string to undefined
          itemCode: rawValue.itemCode,
          workOrderReference: rawValue.workOrderReference,
          quantity: rawValue.quantity,
          description: rawValue.description,
        };
      }),
    };

    if (this.isNewDeliveryNote) {
      this._deliveryNoteProxyService.createDeliveryNote(deliveryNote).subscribe({
        next: async (resp) => {
          if (resp.data !== null) {
            this.deliveryNote = resp.data;
            this.isNewDeliveryNote = false;

            // Upload pending files after delivery note is created
            if (this.pendingFiles.length > 0) {
              try {
                const uploadedFiles = await lastValueFrom(
                  this._deliveryNoteProxyService.uploadFiles(this.pendingFiles, this.deliveryNote.id!),
                );

                if (!this.deliveryNote.files) {
                  this.deliveryNote.files = [];
                }
                this.deliveryNote.files.push(...uploadedFiles);
                this.pendingFiles = [];
              } catch (error) {
                console.error('Error uploading files:', error);
              }
            }

            GlobalMessageUtil.success('Success', 'Delivery Note created successfully');
            this.router.navigate([], {
              relativeTo: this.activatedRoute,
              queryParams: { id: this.deliveryNote.id },
              queryParamsHandling: 'merge',
            });
          }
        },
        error: (e) => {
          console.error(e);
        },
      });
    } else {
      this._deliveryNoteProxyService.updateDeliveryNote(this.deliveryNote.id!, deliveryNote).subscribe({
        next: async () => {
          // Upload pending files after update
          if (this.pendingFiles.length > 0) {
            try {
              const uploadedFiles = await lastValueFrom(
                this._deliveryNoteProxyService.uploadFiles(this.pendingFiles, this.deliveryNote.id!),
              );

              if (!this.deliveryNote.files) {
                this.deliveryNote.files = [];
              }
              this.deliveryNote.files.push(...uploadedFiles);
              this.pendingFiles = [];
            } catch (error) {
              console.error('Error uploading files:', error);
            }
          }

          this.router.navigate(['/delivery-notes']);
          GlobalMessageUtil.success('Success', 'Delivery Note updated successfully');
        },
        error: (e) => {
          console.error(e);
        },
      });
    }
  }

  public cancel() {
    this.router.navigate(['/delivery-notes']);
  }

  public parseDate(date: Date | string, reverse = false): Date | string {
    if (!date) return '';

    if (reverse) {
      return new Date(date);
    }

    if (date instanceof Date) {
      return date.toISOString().split('T')[0];
    }

    const parsedDate = new Date(date);
    if (!isNaN(parsedDate.getTime())) {
      return parsedDate.toISOString().split('T')[0];
    }

    return '';
  }

  viewFile(presignedUrl: string) {
    window.open(presignedUrl, '_blank');
  }

  private async getFilesFromDropEntries(files: NgxFileDropEntry[]): Promise<File[]> {
    const filePromises: Promise<File>[] = [];

    for (const droppedFile of files) {
      if (droppedFile.fileEntry.isFile) {
        const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
        const filePromise = new Promise<File>((resolve, reject) => {
          fileEntry.file(resolve, reject);
        });
        filePromises.push(filePromise);
      }
    }

    return await Promise.all(filePromises);
  }

  async uploadFiles(files: NgxFileDropEntry[]): Promise<void> {
    try {
      const fileList = await this.getFilesFromDropEntries(files);

      // Store files temporarily until save
      this.pendingFiles.push(...fileList);
      GlobalMessageUtil.success('Success', `${fileList.length} file(s) added. Click Save to upload.`);
    } catch (error) {
      console.error(error);
      GlobalMessageUtil.error('Error', 'Error adding files');
    }
  }

  deleteFile(index: number, file: { name: string; presignedUrl?: string; isPending: boolean; file?: File }): void {
    // If file is pending (not saved yet), just remove from pending array
    if (file.isPending) {
      const pendingIndex = this.pendingFiles.findIndex((f) => f.name === file.name);
      if (pendingIndex !== -1) {
        this.pendingFiles.splice(pendingIndex, 1);
        GlobalMessageUtil.success('Success', 'File removed');
      }
      return;
    }

    // If delivery note exists and file is saved, call API to delete from AWS
    if (!this.deliveryNote?.id || !file.presignedUrl) return;

    const s3Object: S3Object = {
      name: file.name,
      presignedUrl: file.presignedUrl,
    };

    this.isLoading++;
    this._deliveryNoteProxyService.deleteFiles([s3Object], this.deliveryNote.id).subscribe({
      next: () => {
        this.isLoading--;
        // Find and remove from deliveryNote.files
        const fileIndex = this.deliveryNote.files?.findIndex((f) => f.name === file.name) ?? -1;
        if (fileIndex !== -1) {
          this.deliveryNote.files?.splice(fileIndex, 1);
        }
        GlobalMessageUtil.success('Success', 'File deleted successfully');
      },
      error: (error) => {
        this.isLoading--;
        console.error('Error deleting file:', error);
      },
    });
  }

  getPendingFileName(file: File): string {
    return file.name;
  }

  getAllFiles(): { name: string; presignedUrl?: string; isPending: boolean; file?: File }[] {
    const savedFiles = (this.deliveryNote?.files || []).map((f) => ({
      name: f.name,
      presignedUrl: f.presignedUrl,
      isPending: false,
    }));
    const pendingFilesList = this.pendingFiles.map((f) => ({
      name: f.name,
      isPending: true,
      file: f,
    }));
    return [...savedFiles, ...pendingFilesList];
  }

  getFileName(nameWithPrefix: string): string {
    const lastSlashIndex = nameWithPrefix.lastIndexOf('/');
    if (lastSlashIndex === -1) {
      return nameWithPrefix;
    }
    return nameWithPrefix.substring(lastSlashIndex + 1);
  }
}
